import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  convertColorToHSL,
  generateThemeColor,
  hex2hsl,
  hsla2hsl,
} from "senf-atomic-design-system/src/styles/helpers";

export const useFeatureAttributes = (formik, index, initialColor) => {
  const { t } = useTranslation();
  // State for the general color
  const [generalColor, setGeneralColor] = useState(initialColor);
  const [generalOpacity, setGeneralOpacity] = useState(null);

  // Excluded properties that should not be included in the attributes list
  const excludedProperties = ["drawType", "color"];

  // Memoized computation of attributes
  const attributes = useMemo(() => {
    // Ensure there is data available
    if (!formik.values.gisData[index]?.data?.features) return [];

    // Extract attributes from features, excluding certain properties
    const allAttributes = formik.values.gisData[index].data.features.flatMap(
      (feature) =>
        Object.keys(feature.properties).filter(
          (property) => !excludedProperties.includes(property),
        ),
    );

    // Deduplicate and structure attributes
    const attributeObjects = [...new Set(allAttributes)].map((attribute) => ({
      value: attribute,
      label:
        attribute === "number"
          ? t("global:number")
          : attribute === "tooltipLabel"
            ? t("global:tooltipLabel")
            : attribute,
    }));

    return attributeObjects;
  }, [formik.values.gisData[index]?.data, excludedProperties, formik.values.t]);

  const handleSubmit = () => {
    if (!formik.values.gisData[index]?.data?.features) return;

    const newFeatures = formik.values.gisData[index]?.data.features.map(
      (feature) => {
        const relatedLayer = formik.values.gisData?.[
          index
        ]?.legendAttributes?.find(
          (layer) =>
            layer.title ===
            feature.properties[formik.values.gisData[index]?.selectedAttribute],
        );

        const selectedAttribute =
          formik.values.gisData[index]?.selectedAttribute;
        const isAttributeNone = selectedAttribute === "none";
        const layerColor = isAttributeNone ? generalColor : relatedLayer?.color;

        const properties = {
          ...feature.properties,
          color: layerColor,
          labelColor: generateThemeColor(convertColorToHSL(layerColor)).color[
            "800"
          ],
        };

        // Only set opacity if there is a valid value
        if (relatedLayer?.opacity !== undefined) {
          properties.opacity = relatedLayer.opacity;
        } else if (isAttributeNone && generalOpacity !== undefined) {
          properties.opacity = generalOpacity;
        }

        // setChangedStyle(false);
        // setTimeout(() => {
        //   setChangedStyle(true);
        // }, 10);

        return { ...feature, properties };
      },
    );

    const newGeodata = { type: "FeatureCollection", features: newFeatures };

    // setMapGisData((prevMapGisData) => ({
    //   ...prevMapGisData,
    //   data: newGeodata,
    // }));
    formik.setFieldValue(`gisData[${index}].data`, newGeodata);
  };

  const handleColorChange = (title, color, opacity) => {
    if (formik.values.gisData[index]?.selectedAttribute === "none") {
      setGeneralColor(color);
      setGeneralOpacity(opacity !== 1 ? opacity : undefined);
    } else {
      formik.setFieldValue(
        `gisData[${index}].legendAttributes`,
        formik.values.gisData[index].legendAttributes.map((layer) => {
          // Check if the current layer matches the title.
          if (layer.title === title) {
            // If opacity is defined and not default, spread it into the layer object.
            const updatedLayer = { ...layer, color };

            if (opacity !== 1) {
              updatedLayer.opacity = opacity;
            } else {
              updatedLayer.opacity = undefined;
            }
            return updatedLayer;
          }
          return layer;
        }),
      );
    }

    handleSubmit();
  };

  return {
    attributes,
    handleSubmit,
    handleColorChange,
    generalColor,
  };
};
